import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import useContent from "@/lib/useContent";
import { useJsonLd } from "@/lib/useSEO";
import { FAQS } from "@/lib/site-data";

export default function FAQ() {
  const faqs = useContent("faqs", FAQS);
  useJsonLd("faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-40 bg-background"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-20">
        {/* Left title column */}
        <div className="md:col-span-5 md:sticky md:top-28 md:self-start">
          <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60 mb-6">
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-primary" />
              <span>Ch. 05 · Practicalities</span>
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.94] tracking-tighter"
          >
            Everything you're probably about to <em className="italic text-primary">ask.</em>
          </motion.h2>
          <p className="mt-6 text-base text-foreground/65 max-w-md font-light leading-relaxed">
            If your question isn't here — the fastest answer is a message to
            our curators.
          </p>
        </div>

        {/* Accordion column */}
        <div className="md:col-span-7">
          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid="faq-accordion"
          >
            {faqs.map((f, i) => (
              <motion.div
                key={f.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
              >
                <AccordionItem
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-foreground/15"
                >
                  <AccordionTrigger className="py-6 md:py-8 hover:no-underline group text-left">
                    <span className="flex items-baseline gap-5 md:gap-8 pr-4">
                      <span className="font-serif italic text-primary text-lg md:text-xl w-6 shrink-0">
                        0{i + 1}
                      </span>
                      <span className="font-serif text-xl md:text-3xl font-light leading-tight group-hover:text-primary transition-colors">
                        {f.q}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent forceMount className="pb-8 pl-11 md:pl-16">
                    <p className="text-base md:text-lg font-light text-foreground/70 leading-relaxed max-w-2xl">
                      {f.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
